// ── Towns' rain totals (or chances) on the futurecast's rain views ──
// The server reads every rendered step at ~9,000 GeoNames places — the
// impact zone town by town, big cities elsewhere in the picture
// (data/futurecast.py city_values). One GeoJSON per run, a label property per
// step; moving through the timeline only swaps which property the layer
// shows. Bigger places win where labels collide, and the layer sits above
// the basemap's own place names, so a town is named once.

import type maplibregl from "maplibre-gl";

const SRC = "future-cities";
const LAYER = "future-cities";
// font stacks the basemap style already uses (its glyph server serves them)
const BOLD = ["Montserrat Medium", "Open Sans Bold", "Noto Sans Regular"];
const REGULAR = ["Montserrat Regular", "Open Sans Regular", "Noto Sans Regular"];

export interface CityValues {
  cities: [string, number, number, number][]; // name, lat, lon, rank (population, seats weighted up)
  steps: { fhr: number; v: number[] }[]; // rain: hundredths of an inch since "now"; chance: %
}

/** Under an inch, tenths ("0.4"); above, half inches ("2", "7.5"): a total
 *  days ahead is not good to the hundredth. Nothing under 0.1". */
export function fmtRain(hundredths: number): string | null {
  const v = hundredths / 100;
  if (v < 0.1) return null;
  if (v < 0.95) return v.toFixed(1); // 0.95 and up reads "1", like every other inch
  const h = Math.round(v * 2) / 2;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

/** To the nearest 5%; nothing under 5%. */
export function fmtChance(pct: number): string | null {
  return pct < 5 ? null : `${Math.round(pct / 5) * 5}%`;
}

/** Features carrying, per step, the label to show there (absent: no label). */
export function cityFeatures(d: CityValues, chance: boolean): GeoJSON.FeatureCollection<GeoJSON.Point> {
  const fmt = chance ? fmtChance : fmtRain;
  return {
    type: "FeatureCollection",
    features: d.cities.map(([n, lat, lon, r], i) => {
      const props: Record<string, string | number> = { n, r };
      for (const s of d.steps) {
        const t = fmt(s.v[i] ?? 0);
        if (t) props[`s${s.fhr}`] = t;
      }
      return { type: "Feature", geometry: { type: "Point", coordinates: [lon, lat] }, properties: props };
    }),
  };
}

function ensureLayer(map: maplibregl.Map) {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    map.addLayer({
      id: LAYER, type: "symbol", source: SRC,
      layout: {
        "text-font": REGULAR,
        "text-size": ["interpolate", ["linear"], ["zoom"], 4, 11, 7, 14, 9, 16],
        "text-line-height": 1.05,
        "text-padding": 3,
        "symbol-sort-key": ["-", 0, ["get", "r"]], // bigger places first
        "text-field": "",
        visibility: "none",
      },
      paint: { "text-color": "#ffffff", "text-halo-color": "rgba(10, 14, 20, 0.92)", "text-halo-width": 1.6 },
    }); // on top of every layer: the towns' values win over the basemap's names
  }
}

/** Show `fhr`'s labels (null: hide the layer). Pass the same `data` object
 *  between steps: it is only re-sent when it changes. */
const sent = new WeakMap<maplibregl.Map, GeoJSON.FeatureCollection>();
export function showCities(map: maplibregl.Map, data: GeoJSON.FeatureCollection | null, fhr: number | null) {
  if (!map.getStyle()) return;
  if (!data || fhr == null) {
    if (map.getLayer(LAYER)) map.setLayoutProperty(LAYER, "visibility", "none");
    return;
  }
  ensureLayer(map);
  if (sent.get(map) !== data) {
    (map.getSource(SRC) as maplibregl.GeoJSONSource).setData(data);
    sent.set(map, data);
  }
  const key = `s${fhr}`;
  map.setLayoutProperty(LAYER, "text-field", [
    "format",
    ["get", key], { "text-font": ["literal", BOLD] },
    "\n", {},
    ["get", "n"], { "font-scale": 0.7, "text-color": "rgba(255, 255, 255, 0.78)" },
  ]);
  map.setFilter(LAYER, ["has", key]);
  map.setLayoutProperty(LAYER, "visibility", "visible");
}
